import { X } from 'lucide-react';

const GENDER_OPTIONS = [
  { value: '', label: 'Any' },
  { value: 'male', label: 'Only boys' },
  { value: 'female', label: 'Only girls' },
  { value: 'everyone', label: 'Everyone' },
];

const CATEGORIES = [
  { id: '', label: 'All categories' },
  { id: 'sports', label: 'Sports' },
  { id: 'fitness', label: 'Fitness' },
  { id: 'music', label: 'Music' },
  { id: 'study', label: 'Study' },
  { id: 'travel', label: 'Travel' },
  { id: 'learning', label: 'Learning' },
  { id: 'social', label: 'Social' },
  { id: 'outdoors', label: 'Outdoors' },
  { id: 'creative', label: 'Creative' },
  { id: 'gaming', label: 'Gaming' },
  { id: 'food', label: 'Food' },
];

const MapFiltersPanel = ({ open, onClose, filters, onChange }) => {
  if (!open) return null;

  const set = (patch) => onChange({ ...filters, ...patch });

  const panelInner = (
    <>
      <div className="flex items-center justify-between border-b border-surface-200 px-4 py-3 dark:border-surface-700 lg:border-0 lg:px-0 lg:pt-0 lg:pb-2">
        <h3 className="text-sm font-extrabold text-surface-900 dark:text-white">Map filters</h3>
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg p-2 text-surface-500 hover:bg-surface-100 dark:hover:bg-surface-800"
          aria-label="Close filters"
        >
          <X size={18} />
        </button>
      </div>

      <div className="flex-1 space-y-5 overflow-y-auto px-4 pb-6 pt-2 lg:px-0">
        <div>
          <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-surface-500 dark:text-surface-400">
            Distance · {filters.radiusKm} km
          </label>
          <input
            type="range"
            min={1}
            max={10}
            step={1}
            value={filters.radiusKm}
            onChange={(e) => set({ radiusKm: parseInt(e.target.value, 10) })}
            className="w-full accent-brand-500"
          />
          <div className="mt-1 flex justify-between text-[10px] text-surface-400">
            <span>1 km</span>
            <span>10 km</span>
          </div>
        </div>

        <div>
          <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-surface-500 dark:text-surface-400">
            Category
          </label>
          <select
            value={filters.category}
            onChange={(e) => set({ category: e.target.value })}
            className="input-field w-full text-sm"
          >
            {CATEGORIES.map((c) => (
              <option key={c.id || 'all'} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-surface-500 dark:text-surface-400">
            Who can join (vibe setting)
          </label>
          <div className="grid grid-cols-2 gap-2">
            {GENDER_OPTIONS.map((o) => (
              <button
                key={o.value || 'any'}
                type="button"
                onClick={() => set({ allowedGender: o.value })}
                className={`rounded-xl border-2 px-3 py-2 text-xs font-bold transition-all ${
                  filters.allowedGender === o.value
                    ? 'border-brand-500 bg-brand-50 text-brand-700 dark:border-brand-400 dark:bg-brand-500/10 dark:text-brand-300'
                    : 'border-surface-200 text-surface-600 dark:border-surface-700 dark:text-surface-400'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>

        <label className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-surface-200 bg-surface-50/80 px-4 py-3 dark:border-surface-700 dark:bg-surface-800/40">
          <span className="text-sm font-semibold text-surface-800 dark:text-surface-200">Verified vibes only</span>
          <input
            type="checkbox"
            checked={filters.verifiedActivitiesOnly}
            onChange={(e) => set({ verifiedActivitiesOnly: e.target.checked })}
            className="h-5 w-5 rounded border-surface-300 text-brand-600 focus:ring-brand-500"
          />
        </label>

        <label className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-surface-200 bg-surface-50/80 px-4 py-3 dark:border-surface-700 dark:bg-surface-800/40">
          <span className="text-sm font-semibold text-surface-800 dark:text-surface-200">Verified host only</span>
          <input
            type="checkbox"
            checked={filters.verifiedHostOnly}
            onChange={(e) => set({ verifiedHostOnly: e.target.checked })}
            className="h-5 w-5 rounded border-surface-300 text-brand-600 focus:ring-brand-500"
          />
        </label>

        <button
          type="button"
          onClick={onClose}
          className="btn-primary w-full !py-3 !text-sm lg:hidden"
        >
          Apply
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile bottom sheet */}
      <div className="fixed inset-0 z-[1300] lg:hidden">
        <button type="button" className="absolute inset-0 bg-black/40" aria-label="Close" onClick={onClose} />
        <div className="absolute bottom-0 left-0 right-0 max-h-[85vh] flex flex-col rounded-t-3xl border border-surface-200 bg-white shadow-2xl dark:border-surface-700 dark:bg-surface-900 animate-slide-up">
          <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-surface-300 dark:bg-surface-600" />
          {panelInner}
        </div>
      </div>

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:flex-col absolute top-0 right-0 z-[600] h-full w-80 border-l border-surface-200/80 bg-white/95 p-4 shadow-xl backdrop-blur-xl dark:border-surface-700/80 dark:bg-surface-900/95">
        {panelInner}
      </aside>
    </>
  );
};

export default MapFiltersPanel;
